import React, { useEffect, useState } from "react";
import { ArrowLeft, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getRequest, putRequest } from "../helpers/api.helper";
import { UPLOAD_URL } from "../utils/constants";
import { getGenderPlaceholder } from "../helpers/matches.helper";

type NotificationRecord = {
  _id: string;
  title?: string;
  message?: string;
  type?: string;
  isRead?: boolean;
  createdAt?: string;
  data?: {
    fullName?: string;
    caste?: string;
    annualIncome?: string;
    citizenship?: string;
    district?: string;
    gender?: string;
    image?: string;
    profileCreatedAt?: string;
    martialId?: string;
    maritalId?: string;
    userId?: string;
    isOtherBureau?: boolean;
    url?: string;
  };
};

const NEW_PROFILE_NOTIFICATION_TITLE =
  "New profile created by Matrimony studio";

const cleanNotificationValue = (value?: string | null) => {
  if (!value) return "";
  const text = value.trim();
  if (!text) return "";
  const normalized = text.toLowerCase();
  return normalized === "null" || normalized === "undefined" ? "" : text;
};

const formatNotificationTime = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNotificationHeading = (notification: NotificationRecord) =>
  notification.type === "NEW_PROFILE_ADDED"
    ? NEW_PROFILE_NOTIFICATION_TITLE
    : notification.title || "Notification";

const getNotificationSummary = (notification: NotificationRecord) => {
  const parts = [
    cleanNotificationValue(notification.data?.fullName),
    cleanNotificationValue(notification.data?.caste),
    cleanNotificationValue(notification.data?.annualIncome),
    cleanNotificationValue(notification.data?.citizenship),
    cleanNotificationValue(notification.data?.district),
  ].filter(Boolean);

  return parts.join(" | ") || notification.message || "";
};

const getNotificationTargetUrl = (notification: NotificationRecord) => {
  if (notification.data?.url) {
    return notification.data.url;
  }

  const profileId =
    notification.data?.martialId ||
    notification.data?.maritalId ||
    notification.data?.userId;

  return profileId ? `/admin/profile/detail/${profileId}` : null;
};

const MyNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRequest("/firebase");
      if (response.success) {
        const items = Array.isArray(response.data) ? response.data : [];
        setNotifications(items);
      } else {
        setError(response.message || "Failed to fetch notifications");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleNotificationClick = async (notification: NotificationRecord) => {
    try {
      await putRequest(`/firebase/${notification._id}/read`, {});
    } catch (err) {
      console.error("Failed to mark notification read", err);
    }

    const targetUrl = getNotificationTargetUrl(notification);
    if (targetUrl) {
      navigate(targetUrl, { state: { openedFromNotification: true } });
      return;
    }
    fetchNotifications();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-4">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="mb-2 flex items-center space-x-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink-100 text-pink-600 transition hover:bg-pink-200"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex min-h-12 items-center">
              <h1 className="text-xl font-bold text-gray-900 sm:text-[28px]">
                Profile Notifications
              </h1>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No notifications yet
            </h3>
            <p className="text-gray-600">
              You will see new profile notifications here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => {
              const profileName = notification.data?.fullName || "New profile";
              const summary = getNotificationSummary(notification);
              const profileTime = formatNotificationTime(
                notification.data?.profileCreatedAt || notification.createdAt
              );
              const badge = notification.data?.isOtherBureau
                ? "Other bureau"
                : "Your website";

              return (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex-shrink-0">
                        <img
                          src={
                            notification.data?.image
                              ? `${UPLOAD_URL}/users/profile_pic/${notification.data.image}`
                              : getGenderPlaceholder(notification.data?.gender)
                          }
                          onError={(event) => {
                            event.currentTarget.src = getGenderPlaceholder(
                              notification.data?.gender
                            );
                          }}
                          alt={profileName}
                          className="w-14 h-14 rounded-full object-cover border border-gray-200"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2 gap-3">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {getNotificationHeading(notification)}
                          </h4>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border border-pink-200 text-pink-700 bg-pink-50">
                            {badge}
                          </span>
                        </div>
                        {summary ? (
                          <p className="text-gray-700 mb-2">{summary}</p>
                        ) : null}
                        {profileTime ? (
                          <p className="text-sm text-gray-500">{profileTime}</p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyNotifications;
